import app from '../../authentication/firebaseStart';
import { ROLES } from '../../authentication/rbac';

const firestore = app.firestore();

// Helper function to execute queries and get total count
export const getTotalCount = async (context, { searchText, searchField, orderDirection }) => {
  try {
    if (searchText && searchField) {
      // For search queries, get the total count of matching documents
      const totalQuery = createQuery({ 
        context, 
        searchText, 
        searchField,
        orderBy: searchField,
        orderDirection 
      });
      // This is needed for pagination to work correctly when going to last page. As we are using old version of firebase we don't have count() method to directly get the count for the query
      const countSnapshot = await totalQuery.get();
      return countSnapshot.size;
    } else {
      // For non-search queries, use the total site count
      return await getSiteCount(
        context.currentUser.claims.companyID, 
        context.currentUser.claims.isSuperAdmin
      );
    }
  } catch (error) {
    console.error('Error getting total count:', error);
    throw error;
  }
};

// Rest of the helper functions remain the same
const executeQuery = async (query) => {
  const snapshot = await query.get();
  return {
    docs: snapshot.docs,
    sites: snapshot.docs.map(doc => ({
      __name__: doc.id,
      ...doc.data()
    })),
    cursors: {
      first: snapshot.docs[0] || null,
      last: snapshot.docs[snapshot.docs.length - 1] || null
    }
  };
};

// Apply pagination to query
const applyPagination = (query, { startAfterDoc, endBeforeDoc, limit }) => {
  let paginatedQuery = query;

  if (startAfterDoc) {
    paginatedQuery = paginatedQuery.startAfter(startAfterDoc);
  } else if (endBeforeDoc) {
    paginatedQuery = paginatedQuery.endBefore(endBeforeDoc);
  }

  if (limit) {
    paginatedQuery = endBeforeDoc ? 
      paginatedQuery.limitToLast(limit) : 
      paginatedQuery.limit(limit);
  }

  return paginatedQuery;
};

// Create query with search and filters
const createQuery = ({ context, searchText, searchField, orderBy, orderDirection }) => {
  const { companyID, isManager, siteName} = context.currentUser.claims;
  let query = firestore
    .collection('tenants')
    .doc(companyID)
    .collection('SiteLocations');

    if (isManager && siteName) {
      query = query.where('siteName', '==', siteName);
    }


  if (searchText && searchField) {
    const searchTerm = searchText.toUpperCase().trim();
    const endTerm = searchTerm + '\uf8ff';
    
    query = query
      .where(searchField, '>=', searchTerm)
      .where(searchField, '<=', endTerm)
      .orderBy(searchField, orderDirection);
  } else {
    query = query.orderBy(orderBy, orderDirection);
  }

  return query;
};

// Main function to fetch sites with pagination
export const fetchSites = async ({
  limit,
  startAfterDoc = null,
  endBeforeDoc = null,
  orderBy = 'lastUpdatedAt',
  orderDirection = 'desc',
  searchText = null,
  searchField = null,
  context,
  totalCount = null
}) => {
  console.log(`Fetching sites with params:`, {
    limit,
    orderBy,
    orderDirection,
    searchText,
    searchField,
    totalCount,
    hasStartAfter: !!startAfterDoc,
    hasEndBefore: !!endBeforeDoc
  });

  try {
    const query = createQuery({ 
      context, 
      searchText, 
      searchField,
      orderBy: searchText ? searchField : orderBy,
      orderDirection 
    });

    const paginatedQuery = applyPagination(query, {
      startAfterDoc,
      endBeforeDoc,
      limit
    });

    const { sites, cursors } = await executeQuery(paginatedQuery);
    
    const count = totalCount ?? await getTotalCount(context, { searchText, searchField, orderDirection });

    return {
      sites,
      totalCount: count,
      cursors
    };
  } catch (error) {
    console.error('Error fetching sites:', error);
    throw error;
  }
};

export const fetchLastPage = async ({
  context,
  limit,
  searchText = null,
  searchField = null,
  orderBy = 'lastUpdatedAt',
  orderDirection = 'desc',
  totalCount = null
}) => {
  try {
    const count = totalCount ?? await getTotalCount(context, { searchText,searchField,orderDirection });

    const totalPages = Math.floor(count / limit);
    const remainingRecords = count - (totalPages * limit);
    const lastPageLimit = remainingRecords > 0 ? remainingRecords : limit;

    const query = createQuery({
      context,
      searchText,
      searchField,
      orderBy: searchText ? searchField : orderBy,
      orderDirection
    });

    const snapshot = await query.limitToLast(lastPageLimit).get();

    return {
      sites: snapshot.docs.map(doc => ({
        __name__: doc.id,
        ...doc.data()
      })),
      cursors: {
        first: snapshot.docs[0] || null,
        last: snapshot.docs[snapshot.docs.length - 1] || null
      },
      totalCount: count
    };
  } catch (error) {
    console.error('Error fetching last page:', error);
    throw error;
  }
};

// Get total site count
export const getSiteCount = async (companyID, isSuperAdmin) => {
  try {
    const sites = (
      await firestore
        .collection('tenants')
        .doc(companyID)
        .collection('SiteLocations')
        .get()
    ).docs.map((doc) => doc.data())
    return sites.length;
  } catch (error) {
    console.error('Error fetching site count:', error);
    throw error;
  }
};

// Get application settings
export const getAppSettings = async (context) => {
  try {
    const companyID = context.currentUser.claims.companyID;
    const doc = await firestore
      .collection('tenants')
      .doc(companyID)
      .collection('Configuration')
      .doc('AppSettings')
      .get();

    return doc.data();
  } catch (error) {
    console.error('Error fetching app settings:', error);
    throw error;
  }
};

// Get SSO parameter mapping status
export const getSSOParameterMapping = async (context) => {
  try {
    const companyID = context.currentUser.claims.companyID;
    const doc = await firestore
      .collection('tenants')
      .doc(companyID)
      .collection('Configuration')
      .doc('AppSettings')
      .collection('SSOSettings')
      .doc('ssoParameterMappingEnable')
      .get();

    return doc.data()?.enabled || false;
  } catch (error) {
    console.error('Error fetching SSO parameter mapping:', error);
    return false;
  }
};

export default {
  fetchSites,
  fetchLastPage,
  getSiteCount,
  getTotalCount,
  getAppSettings,
  getSSOParameterMapping,
};